import type { MultipleQueriesResponse } from '@algolia/client-search'
import dayjs from 'dayjs'
import { HITS_PER_SHOWCASE } from './algolia/constants'
import type { SearchEngineEventItem } from '~/composables/types/searchEngine'

import { buildAlgoliaAnalyticsTags } from '~/utils/searchEngineHelpers'
import { useSearchEngine } from '~/composables/search/useSearchEngine'
import { useTypesenseHandle } from '~/composables/search/useTypesenseHandle'
import { useTypesenseGeoSort } from '~/composables/useTypesenseGeoSort'
import { typesenseMultiSearch } from '~/utils/typesenseClient'
import { adaptTypesenseSearchResponse } from '~/utils/adaptTypesenseSearchResponse'
import { buildHomepageTypesenseQueries } from '~/utils/buildShowcaseTypesenseQueries'

export const useHomepageSearchEngine = async () => {
  const { locale } = useI18n()
  const {
    public: { appEnv },
  } = useRuntimeConfig()

  // KFT-4116: Typesense path ranks by explicit coords (useUserGeo) instead of
  // Algolia's aroundLatLngViaIP. The `import.meta.client` cache-key split makes
  // the client fetch separately from SSR, so client-only geo applies there —
  // and lets the client use the A/B-forced engine straight away (KFT-4115).
  const engine = useSearchEngine({ clientOnly: true })
  const { resolveGeoCoords } = useTypesenseGeoSort()

  // Use useState to create a shared state that persists across composable re-executions
  // This prevents algoliaResults from becoming undefined when the composable is called again
  const algoliaResults = useState<
    MultipleQueriesResponse<SearchEngineEventItem> | undefined
  >('homepage-algolia-results', () => undefined)

  const todayInSecs = dayjs().unix()
  const sevenDaysLaterInSecs = dayjs()
    .add(7, 'day')
    .set('hour', 23)
    .set('minute', 59)
    .set('second', 0)
    .set('millisecond', 0)
    .unix()

  const homePageQueryParams = [
    {
      indexName: getTopEventListIndexName(locale.value),
      hitsPerPage: HITS_PER_SHOWCASE,
      filters: `dates_ts:${todayInSecs} TO ${sevenDaysLaterInSecs} OR dates_private_ts:${todayInSecs} TO ${sevenDaysLaterInSecs}`,
      isBoxes: false,
    },
    {
      indexName: getEventListSortByDateDescIndexName(locale.value),
      hitsPerPage: HITS_PER_SHOWCASE,
      isBoxes: false,
    },
    {
      indexName: getTopGiftIdeasIndexName(locale.value),
      hitsPerPage: HITS_PER_SHOWCASE * 2,
      isBoxes: false,
    },
    {
      indexName: getEventListIndexName(locale.value),
      filters: '(has_promotions:true)',
      hitsPerPage: HITS_PER_SHOWCASE,
      isBoxes: false,
    },
    {
      indexName: getBoxesIndexName(locale.value),
      hitsPerPage: HITS_PER_SHOWCASE,
      isBoxes: true,
    },
  ]

  const homePageQueries = homePageQueryParams.map((params) => {
    let filters = params.isBoxes ? '' : 'has_events:true'
    delete params.isBoxes

    if (params.filters) {
      filters += ` AND (${params.filters})`
    }

    return {
      ...params,
      clickAnalytics: true,
      analyticsTags: buildAlgoliaAnalyticsTags(appEnv),
      filters,
      aroundLatLngViaIP: import.meta.client,
      aroundRadius: 'all',
      aroundPrecision: [
        { from: 0, value: 15000 },
        { from: 30000, value: 30000 },
        { from: 150000, value: 100000 },
      ],
    }
  })

  const {
    data: algoliaResultsRaw,
    pending,
    status,
  } = await useLazyAsyncData<MultipleQueriesResponse<SearchEngineEventItem>>(
    () => `algoliaResults-homepage-${engine.value}-${import.meta.client}`,
    async () => {
      if (engine.value === 'typesense') {
        const handle = useTypesenseHandle()
        const coords = await resolveGeoCoords()
        const queries = buildHomepageTypesenseQueries({
          locale: locale.value,
          coords,
          todayTs: todayInSecs,
          weekEndTs: sevenDaysLaterInSecs,
          hitsPerShowcase: HITS_PER_SHOWCASE,
        })
        const results = await typesenseMultiSearch<SearchEngineEventItem>(
          queries,
          handle
        )
        return {
          results: results.map((result, index) =>
            adaptTypesenseSearchResponse<SearchEngineEventItem>(result, {
              query: '*',
              perPage: queries[index]!.per_page ?? HITS_PER_SHOWCASE,
            })
          ),
        } as unknown as MultipleQueriesResponse<SearchEngineEventItem>
      }

      return useAlgoliaSearchClient().search(homePageQueries)
    }
  )

  // Initialize algoliaResults from useState if not already set, or use existing value
  // This ensures we preserve the value across composable re-executions
  if (!algoliaResults.value && algoliaResultsRaw.value) {
    algoliaResults.value = algoliaResultsRaw.value
  }

  // Watch algoliaResultsRaw and update algoliaResults, but never set it to undefined
  // Since algoliaResults is now from useState, it persists across composable re-executions
  watch(
    algoliaResultsRaw,
    (newVal) => {
      // Only update if we have new data - preserve previous value if newVal is undefined
      if (newVal) {
        algoliaResults.value = newVal
      }
      // If newVal is undefined, algoliaResults.value keeps its previous value (from useState)
    },
    { immediate: true }
  )

  return {
    algoliaResults,
    pending,
    status,
  }
}
